"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  CallScoringService,
  type CallScoringDraftEmailFeedbackRequest,
  type CallScoringDraftEmailFeedbackResponse,
  type CallScoringDraftEmailResponse,
  type CallScoringDraftEmailSuccessResponse,
} from "@/api-client/call-scoring";
import { getQueryKey, QUERY_KEYS } from "@/lib/query-keys";

type DraftEmailFeedbackRequest = CallScoringDraftEmailFeedbackRequest;
type DraftEmailFeedbackResponse = CallScoringDraftEmailFeedbackResponse;

interface UseCallScoringDraftEmailFeedbackParams {
  meetingId: number | null | undefined;
}

export function useCallScoringDraftEmailFeedback({ meetingId }: UseCallScoringDraftEmailFeedbackParams) {
  const queryClient = useQueryClient();

  const draftEmailQueryKey = useMemo(() => {
    return getQueryKey(QUERY_KEYS.CALL_SCORING_DRAFT_EMAIL, {
      meetingId: meetingId ?? 0,
    });
  }, [meetingId]);

  return useMutation<DraftEmailFeedbackResponse, Error, DraftEmailFeedbackRequest>({
    mutationFn: async (payload) => {
      if (!meetingId) throw new Error("Meeting ID is required");
      return CallScoringService.submitDraftEmailFeedback(meetingId, payload);
    },
    onSuccess: (response) => {
      if (!meetingId) return;
      // Instead of refetching, patch the cached draft email response directly.
      queryClient.setQueryData<CallScoringDraftEmailResponse>([draftEmailQueryKey], (old) => {
        if (!old) return old;
        if (!("subject" in old && "message" in old)) return old;
        const existingDraft = old as CallScoringDraftEmailSuccessResponse;
        const { section, is_liked, is_disliked } = response;

        if (section === "subject") {
          return {
            ...existingDraft,
            subject: {
              ...existingDraft.subject,
              is_liked,
              is_disliked,
            },
          };
        }

        return {
          ...existingDraft,
          message: {
            ...existingDraft.message,
            is_liked,
            is_disliked,
          },
        };
      });
    },
  });
}

